/* eslint-disable sort-keys */
import {
  makeAutoObservable,
  configure,
  IObservableArray,
  reaction,
  when,
} from 'mobx';
import LogRocket from 'logrocket';
import { IntercomProps } from 'react-use-intercom';
import * as Sentry from '@sentry/react';
import { IAlert } from '@types';
import { destroy, get, post, put } from '@lib/ApiClient';
import { Device, OrganizationWith } from '@lib/Organization';
import { navigate } from '@lib/history';
import { IAppError, IMonitor, OperatingRoom } from '@types';
import { User } from '@lib/User';
import { UserCable, createUserCable } from '@lib/userCable';
import createCable, { resetCable } from '@lib/cable';
import {
  SESSION_EXPIRED_EVENT,
  USER_KEY,
  authToken,
  clearStoredSession,
  resetSessionExpiry,
  sessionIsExpiring,
} from '@lib/session';

configure({
  enforceActions: 'never', // let TypeScript keep us safe
});

const store = {
  adminView: false,
  bootedIntercom: false,
  cable: undefined,
  errors: [] as IAppError[],
  loggingOut: false,
  monitors: [] as IObservableArray<IMonitor>,
  organizations: [] as IObservableArray<OrganizationWith>,
  performedInitialUserFetch: false,
  postLoginDestination: '/dashboard' as string,
  saving: false,
  showLanding: true,
  stopIntercom: null as () => void,
  user: null as User,
  userCableReady: false,
  cableRetries: 0,
  cableInstance: null as any,
  userCableInstance: null as UserCable | null,

  get userCable(): UserCable {
    if (store.user && store.cable && authToken()) {
      // Return cached instance if available
      if (!store.userCableInstance) {
        store.userCableInstance = createUserCable(store.cable);
      }
      return store.userCableInstance;
    }
    return null;
  },

  get intercomOpts() {
    return store.user
      ? {
        email: store.user.email,
        userId: store.user.id,
        name: stripPrefixFromName(store.user.name),
        phone: store.user.phone,
        company: store.currentOrganization
          ? {
            companyId: store.currentOrganization.id,
            name: store.currentOrganization.name,
          }
          : null,
      }
      : { email: null, userId: null };
  },

  get currentOrganization(): OrganizationWith | null {
    return store.user
      ? store.organizations.find(
        ({ id }) => id === store.user.currentOrganizationId,
      )
      : null;
  },

  get alerts(): IAlert[] {
    return store.monitors.reduce(
      (alerts, monitor) => alerts.concat(monitor.alerts),
      [],
    );
  },

  /**
   * Determines who the user actually is, including all the user data
   * @param {boolean?} override - If true, will ignore the user object in localStorage
   */
  async loadCurrentUser(override?: boolean) {
    Sentry.addBreadcrumb({ message: 'loading current user' });
    store.userCableReady = false;

    // No credential means there is nothing to load. Without this the app would
    // fire three requests it knew would fail, purely to discover it is logged
    // out, and show their failures as errors on the way to the login form.
    if (!authToken()) {
      store.resetLocalSession();
      store.performedInitialUserFetch = true;
      return;
    }

    // Only create new cable if we don't have one
    if (!store.cableInstance) {
      store.cable = createCable();
      store.cableInstance = store.cable;
    }

    try {
      const promises: Promise<any>[] = [
        store.loadOrganizations(),
        store.loadMonitors(),
      ];

      const user = localStorage.getItem(USER_KEY);

      if (!override && user) {
        await Promise.all(promises);
        store.user = JSON.parse(user);
      } else {
        promises.push(get<User>('current_user'));
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [_, __, storeUser] = await Promise.all(promises);
        store.user = storeUser;
      }
      localStorage.setItem(USER_KEY, JSON.stringify(store.user));

      if (store.user) {
        Sentry.setUser({
          id: store.user.id,
          email: store.user.email,
        });

        // currentOrganization is derived from the loaded organizations list and
        // is legitimately null for a user with no membership yet. Reading .name
        // off it threw a TypeError that was NOT a 403, so it escaped the handler
        // below and surfaced as a generic error instead of a working dashboard.
        if (store.currentOrganization) {
          Sentry.setContext('currentOrganization', {
            name: store.currentOrganization.name,
            id: store.currentOrganization.id,
          });
        }

        LogRocket.identify(store.user.id, {
          name: store.user.name,
          email: store.user.email,
        });
      }

      // The live feed is important but it is NOT a precondition for showing the
      // dashboard. performWithResponse waits on `when(() => userCableReady)`,
      // which never resolves if the socket was rejected — so this line could
      // hang forever, and App's loading spinner with it. A clinician then sees
      // a spinner rather than vitals. Bounded, and failure is reported through
      // userCableReady instead of blocking the render.
      try {
        await store.connectUserCable();
      } catch {
        Sentry.addBreadcrumb({ message: 'live feed unavailable at load' });
      }

      Sentry.addBreadcrumb({ message: 'loaded current user' });
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 401) {
        // ApiClient has already confirmed the session is dead and cleared it.
        store.resetLocalSession();
      } else {
        // Anything else is a real fault worth reporting, but it must not become
        // an unhandled rejection — that path renders an error toast over the
        // monitoring UI and tells the clinician nothing useful.
        Sentry.captureException(e);
        store.errors.push(e as IAppError);
      }
    } finally {
      store.performedInitialUserFetch = true;
    }
  },

  /** Subscribes the user channel, giving up rather than waiting forever. */
  async connectUserCable(timeoutMs = 8000) {
    const cable = store.userCable;
    if (!cable) return;

    await Promise.race([
      cable.performWithResponse('hello'),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('cable connect timeout')), timeoutMs),
      ),
    ]);
  },

  async loadOrganizations() {
    store.organizations.replace(await get<OrganizationWith[]>('organizations'));
  },

  async loadMonitors() {
    store.monitors.replace(await get<IMonitor[]>('monitors'));
  },

  monitorForRoom({ id }: OperatingRoom) {
    return store.monitors.find(({ operatingRoomId }) => operatingRoomId === id);
  },

  facilityForRoom({ id }: OperatingRoom) {
    return store.currentOrganization.facilities.find(({ operatingRooms }) => {
      return operatingRooms.some(candidate => id === candidate.id);
    });
  },

  roomForMonitor({ operatingRoomId }: IMonitor): OperatingRoom {
    for (const facility of store.currentOrganization.facilities) {
      for (const room of facility.operatingRooms) {
        if (room.id === operatingRoomId) {
          return room;
        }
      }
    }

    throw new Error(`no operating room with id ${operatingRoomId}`);
  },

  roomForDevice({ operatingRoomId }: Device): OperatingRoom {
    if (!operatingRoomId) return null;

    for (const facility of store.currentOrganization.facilities) {
      for (const room of facility.operatingRooms) {
        if (room.id === operatingRoomId) {
          return room;
        }
      }
    }

    throw new Error(`no operating room with id ${operatingRoomId}`);
  },

  get sortedMonitors(): IMonitor[] {
    return store.monitors.slice().sort((a, b) => {
      /**
       * Sorting priority should be:
       * by alerts
       * by status
       * * active
       * * standby
       * * off
       * then finally by name
       */

      if (a.alerts.length !== b.alerts.length) {
        return b.alerts.length - a.alerts.length;
      }

      const aRoom = store.roomForMonitor(a);
      const bRoom = store.roomForMonitor(b);

      if (aRoom.status !== bRoom.status) {
        switch (true) {
          case aRoom.status === 'active':
            return -1;

          case bRoom.status === 'active':
            return 1;

          case aRoom.status === 'standby':
            return -1;

          case bRoom.status === 'standby':
            return 1;
        }
      }

      return aRoom.name.localeCompare(bRoom.name);
    });
  },

  async setOrganization(organization_id: string) {
    store.userCable.perform('clear_monitors', {});
    store.monitors = [] as IObservableArray<IMonitor>;
    store.user.currentOrganizationId = organization_id;
    await put('current_organization', { organization_id });
    await Promise.all([store.loadOrganizations(), store.loadMonitors()]);
    localStorage.setItem(USER_KEY, JSON.stringify(store.user));
  },

  async reconnectCable() {
    // Reset cable instances
    resetCable();
    store.cableInstance = null;
    store.userCableInstance = null;
    store.userCableReady = false;
    
    // Create new cable instance
    store.cable = createCable();
    store.cableInstance = store.cable;
    
    // Reconnect user cable
    if (store.user && authToken()) {
      try {
        await store.connectUserCable();

        // Reload monitors to ensure data is fresh
        await store.loadMonitors();
        store.cableRetries = 0;
      } catch (error) {
        // Back off instead of hammering. This used to be a flat 5s retry with
        // no ceiling, so a session that had expired — where every attempt is
        // guaranteed to be rejected — became a permanent reconnect loop
        // against the API, from every open dashboard, forever.
        store.cableRetries += 1;

        if (store.cableRetries > 6) {
          Sentry.addBreadcrumb({ message: 'giving up on cable reconnect' });
          return;
        }

        const delay = Math.min(30000, 2000 * 2 ** (store.cableRetries - 1));

        setTimeout(() => {
          if (store.user && !store.userCableReady && authToken()) {
            store.reconnectCable();
          }
        }, delay);
      }
    }
  },

  async logOut({ navigateTo } = { navigateTo: '/' || null }) {
    store.loggingOut = true;

    try {
      store.userCable?.unsubscribe();
      // Best effort. Telling the server is polite, but if the session is
      // already dead this call fails — and it used to take the rest of logout
      // down with it, so the stale token was never removed and the user was
      // stuck in a state they could not sign out of. Local teardown is what
      // actually matters and now always runs.
      await destroy('session');
    } catch {
      // ignored on purpose — see above
    } finally {
      store.resetLocalSession();

      if (navigateTo) {
        navigate(navigateTo);
      }

      store.loggingOut = false;
    }
  },

  /** Everything that must be true after signing out, however we got here. */
  resetLocalSession() {
    clearStoredSession();
    store.user = null;
    store.performedInitialUserFetch = false;
    store.monitors.clear();
    store.organizations.clear();
    store.stopIntercom && store.stopIntercom();
    store.stopIntercom = null;
    store.userCableReady = false;

    resetCable();
    store.cableInstance = null;
    store.userCableInstance = null;

    Sentry.configureScope(scope => scope.setUser(null));
  },

  async doPostLogin() {
    // Re-arm the one-shot expiry guard: this browser now holds a fresh
    // credential, so a LATER expiry must be able to fire again.
    resetSessionExpiry();
    store.cableRetries = 0;
    await store.loadCurrentUser(true);
  },

  startIntercom(
    boot: (opts: IntercomProps) => void,
    update: (opts: IntercomProps) => void,
    hardShutdown: () => void,
  ) {
    if (store.stopIntercom) {
      return;
    }

    boot(store.intercomOpts);

    const disposer = reaction(
      () => store.intercomOpts,
      opts => {
        update(opts);
      },
    );

    store.stopIntercom = () => {
      disposer();
      hardShutdown();
      store.stopIntercom = null;
    };
  },

  async createMonitor(room: OperatingRoom) {
    await post('monitors', {
      operatingRoomId: room.id,
    });
  },
};

makeAutoObservable(store, null, { autoBind: true, name: 'global store' });

when(
  () => store.performedInitialUserFetch,
  () => {
    reaction(
      () => store.monitors.filter(monitor => !!monitor.discardedAt),
      discardedMonitors =>
        discardedMonitors.forEach(monitor => {
          store.monitors.remove(monitor);
        }),
    );
  },
);

export default store;

// if (process.env.MODE === 'development') {
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.store = store;
// }

export class RejectionError extends Error {
  rejection: PromiseRejectionEvent;
}

// A dead session is a routine event, not a fault. When one expires, drop the
// local state and let the router fall through to the login form — silently.
window.addEventListener(SESSION_EXPIRED_EVENT, () => {
  Sentry.addBreadcrumb({ message: 'session expired, returning to login' });
  store.resetLocalSession();
  store.performedInitialUserFetch = true;
  navigate('/');
});

window.addEventListener('unhandledrejection', rejection => {
  // Auth failures are handled by the session machinery above and end in a
  // login prompt. Surfacing them here as well put a red "Error detected" box
  // over the monitoring UI for something the user can do nothing about — and
  // in production Sentry's beforeSend pushed the SAME error a second time, so
  // one expiry could produce several notifications.
  const status = (rejection.reason as any)?.response?.status;
  if (status === 401 || status === 403 || sessionIsExpiring()) {
    rejection.preventDefault();
    return;
  }

  const e = new RejectionError('promise rejection') as IAppError;
  e.rejection = rejection;
  store.errors.push(e);
});

function stripPrefixFromName(name: string) {
  return name.replace(/^.+\.\s*/, '');
}
